import { css, keyframes } from '@emotion/react'
import { Icon } from '@iconify/react'
import { Button, ServiceLogo, Stack, theme, Typography, useResponsive } from '@nexans/react-ui-kit'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'

import ForceCountryDialog from '@/components/elements/ForceCountryDialog'
import { Logo } from '@/components/svg'
import { APP_ENV, APP_TITLE } from '@/config'

export function Auth() {
  const { t } = useTranslation(['auth', 'common'])
  const [step, setStep] = useState<'carousel' | 'form'>('carousel')
  const [forceCountryDialogOpened, setForceCountryDialogOpened] = useState(false)

  const { isMobile, isSmallTablet } = useResponsive()

  const isSmallDevice = isMobile || isSmallTablet
  const canForceCountry = ['dev', 'qal'].includes(APP_ENV)

  return (
    <Stack
      align="center"
      direction="horizontal"
      justify="space-between"
      p={!isSmallDevice ? '2xl' : undefined}
      spacing="xl"
      fullHeight
      fullWidth
    >
      {(!isSmallDevice || step === 'carousel') && (
        <Stack align="center" css={{ flex: 2, position: 'relative' }} fullHeight>
          {isSmallDevice && (
            <Stack
              align="center"
              css={{ paddingBottom: 100, paddingTop: 100, position: 'absolute', zIndex: 10 }}
              justify="space-between"
              fullHeight
              fullWidth
            >
              <a href="https://www.nexans.fr/fr/">
                <Logo fill={theme.colors.gray[0]} height={24} />
              </a>
              <ServiceLogo color={theme.colors.gray[0]} size={28} title={APP_TITLE} />
              <Button
                color="destructive"
                css={{ whiteSpace: 'nowrap', width: '90%' }}
                variant="contained"
                onClick={() => setStep('form')}
              >
                {t('actions.join')}
              </Button>
            </Stack>
          )}
          <div css={styles.carousel} style={{ borderRadius: !isSmallDevice ? 20 : 0 }}>
            <div css={styles.slides}>
              <div css={styles.slide}>
                <img alt="Mobiway" css={styles.image} src="/assets/images/home-1.jpg" />
                <div css={styles.mask} />
                {!isSmallDevice && (
                  <div css={styles.slideLabel}>
                    <Typography css={styles.typo} variant="h4">
                      {t('labels.slide_1')}
                    </Typography>
                    <Stack direction="horizontal" mt="xl" spacing="sm">
                      <div css={styles.line} />
                      <div css={styles.dot} />
                    </Stack>
                  </div>
                )}
              </div>
              <div css={styles.slide}>
                <img alt="Mobiway" css={styles.image} src="/assets/images/home-2.jpg" />
                <div css={styles.mask} />
                {!isSmallDevice && (
                  <div css={styles.slideLabel}>
                    <Typography css={styles.typo} variant="h4">
                      {t('labels.slide_2')}
                    </Typography>
                    <Stack direction="horizontal" mt="xl" spacing="sm">
                      <div css={styles.dot} />
                      <div css={styles.line} />
                    </Stack>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Stack>
      )}
      {(!isSmallDevice || step === 'form') && (
        <Stack css={styles.formPanel} fullHeight>
          {canForceCountry && (
            <Button
              color="primary"
              css={styles.forceCountryButton}
              icon={<Icon height={20} icon="mdi:world-wide-web" width={20} />}
              size="small"
              variant="text"
              onClick={() => setForceCountryDialogOpened(true)}
            >
              {t('common:navbar.force_country')}
            </Button>
          )}
          <Stack css={styles.formScroll}>
            <Stack align="center" css={styles.formViewport} px="lg" py={isSmallDevice ? '2xl' : undefined} fullWidth>
              <Stack css={styles.formStepContainer} fullHeight fullWidth>
                <Outlet />
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      )}
      <ForceCountryDialog opened={forceCountryDialogOpened} onClose={() => setForceCountryDialogOpened(false)} />
    </Stack>
  )
}

const slide = keyframes`
0% { transform: translateX(0); }
45% { transform: translateX(0); }
50% { transform: translateX(-50%); }
95% { transform: translateX(-50%); }
100% { transform: translateX(0); }
`

const styles = {
  carousel: css({
    position: 'relative',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
  }),
  formPanel: css({
    flex: 3,
    minHeight: 0,
    position: 'relative',
  }),
  forceCountryButton: css({
    position: 'absolute',
    top: 16,
    right: 16,
    zIndex: 1,
  }),
  formScroll: css({
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
  }),
  formViewport: css({
    minHeight: '100%',
  }),
  formStepContainer: css({
    width: '100%',
    maxWidth: 400,
    minHeight: '100%',
  }),
  slides: css({
    display: 'flex',
    width: '200%',
    height: '100%',
    maxHeight: '100%',
    animation: `${slide} 14s infinite`,
  }),
  slide: css({
    position: 'relative',
    width: '50%',
    height: '100%',
    maxHeight: '100%',
  }),
  image: css({
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
  }),
  slideLabel: css({
    position: 'absolute',
    bottom: 100,
    left: 50,
    width: 'calc(100% - 200px)',
    zIndex: 2,
  }),
  typo: css({
    color: theme.colors.gray[0],
  }),
  line: css({
    width: 24,
    height: 5,
    backgroundColor: theme.colors.gray[0],
    borderRadius: 7,
  }),
  dot: css({
    width: 5,
    height: 5,
    backgroundColor: theme.colors.gray[0],
    borderRadius: 7,
  }),
  mask: css({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0,0,0,0.2)',
    zIndex: 1,
  }),
}
